// The History list without a status filter. The list endpoint matches one
// status exactly, so "everything finished" is one request per finished status,
// merged newest first.

/** The statuses of a proposal that is finished, in the order of the tabs. */
export const HISTORY_STATUSES = [
  'completed',
  'failed',
  'denied',
  'expired',
  'rolled_back',
] as const;

export interface HistoryPage<T> {
  data: T[];
  last_page: number;
}

/** The statuses whose request failed (null), in the order they were asked. */
export function failedHistoryStatuses(
  pages: Array<HistoryPage<unknown> | null>,
  statuses: readonly string[] = HISTORY_STATUSES
): string[] {
  return statuses.filter((_status, index) => !pages[index]);
}

/**
 * What the list shows after loading: everything ('ok'), part of it with a
 * note ('partial'), or the error state because nothing could be loaded.
 */
export type HistoryLoadState = 'ok' | 'partial' | 'failed';

export function historyLoadState(
  failedCount: number,
  requestedCount: number
): HistoryLoadState {
  if (failedCount <= 0) return 'ok';
  return failedCount >= requestedCount ? 'failed' : 'partial';
}

/**
 * One page of the merged list: the rows of every status's page, newest first.
 * There are as many pages as the longest of the lists has; a status that could
 * not be loaded (null) is left out.
 */
export function mergeHistoryPages<T extends { id: string; created_at: string }>(
  pages: Array<HistoryPage<T> | null>
): HistoryPage<T> {
  const seen = new Set<string>();
  const data: T[] = [];
  let lastPage = 1;
  pages.forEach((page) => {
    if (!page) return;
    lastPage = Math.max(lastPage, page.last_page || 1);
    (page.data || []).forEach((row) => {
      if (seen.has(row.id)) return;
      seen.add(row.id);
      data.push(row);
    });
  });
  data.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
  return { data, last_page: lastPage };
}
